import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { 
  BarChart3, Users, ImageMinus, Camera, Sparkles, TrendingUp,
  Calendar, RefreshCw, LogIn, LogOut, UserPlus, Mail, Key,
  Trash2, Eye, EyeOff, Send, Lock, AlertCircle, Sun, Moon, Globe, Edit, Plus,
  FileText, Clock
} from "lucide-react";

export default function AdminPage() {
  // Theme state
  const [theme, setTheme] = useState(localStorage.getItem("admin_theme") || "dark");
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  
  // Forgot password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  
  // Dashboard state
  const [activeTab, setActiveTab] = useState("metrics");
  const [metrics, setMetrics] = useState(null);
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  
  // Branch creation
  const [branchCount, setBranchCount] = useState(1);
  const [branchName, setBranchName] = useState("");
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  
  // Branch editing
  const [showEditBranch, setShowEditBranch] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editBranchName, setEditBranchName] = useState("");
  
  // Branch usage history
  const [showBranchHistory, setShowBranchHistory] = useState(false);
  const [selectedBranchHistory, setSelectedBranchHistory] = useState(null);
  
  // Staff creation
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [newStaffName, setNewStaffName] = useState("");
  const [newStaffEmail, setNewStaffEmail] = useState("");
  const [newStaffRole, setNewStaffRole] = useState("staff");
  const [createdStaffPassword, setCreatedStaffPassword] = useState("");
  
  // Email preview
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  
  // Login history dialog
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [selectedStaffHistory, setSelectedStaffHistory] = useState(null);
  
  // Passport Report state
  const [passportReport, setPassportReport] = useState(null);
  const [reportDays, setReportDays] = useState(7);
  const [reportLoading, setReportLoading] = useState(false);

  // Apply theme
  useEffect(() => {
    localStorage.setItem("admin_theme", theme);
    if (theme === "light") {
      document.documentElement.classList.add("light-theme");
      document.documentElement.classList.remove("dark-theme");
    } else {
      document.documentElement.classList.add("dark-theme");
      document.documentElement.classList.remove("light-theme");
    }
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === "dark" ? "light" : "dark");

  // Theme-based classes
  const themeClasses = {
    bg: theme === "light" ? "bg-gray-50" : "bg-[#07070a]",
    card: theme === "light" ? "bg-white border-gray-200" : "bg-zinc-950/50 border-white/10",
    text: theme === "light" ? "text-gray-900" : "text-zinc-100",
    textMuted: theme === "light" ? "text-gray-500" : "text-zinc-400",
    input: theme === "light" ? "bg-white border-gray-300 text-gray-900" : "bg-white/10 border-white/20 text-white",
  };

  // Configure API with token
  const authApi = useCallback(() => ({
    get: (url, config = {}) => api.get(url, { ...config, headers: { ...config.headers, Authorization: `Bearer ${token}` } }),
    post: (url, data, config = {}) => api.post(url, data, { ...config, headers: { ...config.headers, Authorization: `Bearer ${token}` } }),
    delete: (url, config = {}) => api.delete(url, { ...config, headers: { ...config.headers, Authorization: `Bearer ${token}` } })
  }), [token]);

  useEffect(() => {
    const savedToken = localStorage.getItem("admin_token");
    if (savedToken) { setToken(savedToken); verifyToken(savedToken); }
  }, []);

  const verifyToken = async (t) => {
    try {
      const res = await api.get("/auth/me", { headers: { Authorization: `Bearer ${t}` } });
      setCurrentUser(res.data.user);
      setIsLoggedIn(true);
      loadDashboard(t);
    } catch (e) {
      localStorage.removeItem("admin_token");
      setToken("");
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      const res = await api.post("/auth/login", { email: loginEmail, password: loginPassword });
      const { access_token, user } = res.data;
      localStorage.setItem("admin_token", access_token);
      setToken(access_token);
      setCurrentUser(user);
      setIsLoggedIn(true);
      toast({ title: "Welcome!", description: `Logged in as ${user.name}` });
      loadDashboard(access_token);
    } catch (e) {
      toast({ title: "Login failed", description: e?.response?.data?.detail || "Invalid credentials", variant: "destructive" });
    } finally { setLoginBusy(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(""); setIsLoggedIn(false); setCurrentUser(null);
    setMetrics(null); setBranches([]); setStaff([]);
    toast({ title: "Logged out", description: "You have been logged out successfully" });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast({ title: "Password Reset Sent", description: "If the email exists, you will receive a password reset email shortly" });
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed to send reset email", variant: "destructive" });
    } finally { setLoginBusy(false); }
  };

  const loadDashboard = async (t) => {
    const authToken = t || token;
    setBusy(true);
    try {
      const [metricsRes, branchesRes, staffRes] = await Promise.all([
        api.get("/admin/metrics", { headers: { Authorization: `Bearer ${authToken}` } }),
        api.get("/admin/branches/list", { headers: { Authorization: `Bearer ${authToken}` } }),
        api.get("/admin/staff/list", { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      setMetrics(metricsRes.data);
      setBranches(branchesRes.data.branches || []);
      setStaff(staffRes.data.staff || []);
    } catch (e) {
      toast({ title: "Error loading dashboard", description: e?.response?.data?.detail || "Failed to load data", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const createBranches = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const form = new FormData();
      form.append("count", String(branchCount));
      if (branchName.trim()) form.append("name", branchName.trim());
      
      const res = await authApi().post("/admin/branches/create", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Success", description: `Created ${res.data.created?.length || branchCount} branch code(s)` });
      setShowCreateBranch(false);
      setBranchName("");
      setBranchCount(1);
      loadDashboard();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed to create branches", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const updateBranch = async (e) => {
    e?.preventDefault();
    if (!editingBranch) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("code", editingBranch.code);
      form.append("name", editBranchName);
      
      await authApi().post("/admin/branches/update", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Success", description: "Branch updated successfully" });
      setShowEditBranch(false);
      setEditingBranch(null);
      loadDashboard();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed to update branch", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const viewBranchHistory = (branch) => {
    setSelectedBranchHistory(branch);
    setShowBranchHistory(true);
  };

  const startEditBranch = (branch) => {
    setEditingBranch(branch);
    setEditBranchName(branch.name || "");
    setShowEditBranch(true);
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authApi().post("/admin/staff/create", { email: newStaffEmail, name: newStaffName, role: newStaffRole });
      setCreatedStaffPassword(res.data.generated_password);
      const previewRes = await authApi().post("/admin/staff/preview-email", { to_email: newStaffEmail, staff_name: newStaffName, password: res.data.generated_password, role: newStaffRole, email_type: "welcome" });
      setEmailPreviewData({ ...previewRes.data, password: res.data.generated_password, staff_name: newStaffName, role: newStaffRole });
      setShowEmailPreview(true);
      setShowCreateStaff(false);
      toast({ title: "Staff Created", description: "Review and send credentials email" });
      loadDashboard();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed to create staff", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const handleResetPassword = async (email, name) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("email", email);
      const res = await authApi().post("/admin/staff/reset-password", form, { headers: { "Content-Type": "multipart/form-data" } });
      const previewRes = await authApi().post("/admin/staff/preview-email", { to_email: email, staff_name: name, password: res.data.new_password, role: "staff", email_type: "reset" });
      setEmailPreviewData({ ...previewRes.data, password: res.data.new_password, staff_name: name, email_type: "reset" });
      setShowEmailPreview(true);
      toast({ title: "Password Reset", description: "Review and send new credentials" });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed to reset password", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const handleSendEmail = async () => {
    if (!emailPreviewData) return;
    setEmailSending(true);
    try {
      const form = new FormData();
      form.append("email", emailPreviewData.to_email);
      form.append("staff_name", emailPreviewData.staff_name);
      form.append("password", emailPreviewData.password);
      form.append("role", emailPreviewData.role || "staff");
      form.append("email_type", emailPreviewData.email_type || "welcome");
      const res = await authApi().post("/admin/staff/send-credentials", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.success) {
        toast({ title: "✅ Email Sent Successfully!", description: `Credentials sent to ${emailPreviewData.to_email}` });
        setShowEmailPreview(false);
        setEmailPreviewData(null);
        setNewStaffName(""); setNewStaffEmail(""); setCreatedStaffPassword("");
      } else throw new Error(res.data.message);
    } catch (e) {
      toast({ title: "Failed to send email", description: e?.response?.data?.detail || e.message || "Email sending failed", variant: "destructive" });
    } finally { setEmailSending(false); }
  };

  const handleDeleteStaff = async (email) => {
    if (!confirm(`Delete staff member ${email}?`)) return;
    setBusy(true);
    try {
      await authApi().delete(`/admin/staff/${encodeURIComponent(email)}`);
      toast({ title: "Deleted", description: `Staff member ${email} removed` });
      loadDashboard();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed to delete staff", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const viewLoginHistory = (staffMember) => {
    setSelectedStaffHistory(staffMember);
    setShowLoginHistory(true);
  };

  // Load passport report
  const loadPassportReport = async (days = reportDays) => {
    setReportLoading(true);
    try {
      const res = await authApi().get(`/admin/passport-report?days=${days}`);
      setPassportReport(res.data);
    } catch (e) {
      toast({ 
        title: "Error loading report", 
        description: e?.response?.data?.detail || "Failed to load passport report", 
        variant: "destructive" 
      });
    } finally {
      setReportLoading(false);
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text}`} data-testid="admin-login-page">
        <TopNav />
        <div className="flex items-center justify-center min-h-[80vh] px-4">
          <Card className={`w-full max-w-md rounded-3xl ${themeClasses.card}`}>
            <CardHeader className="text-center">
              <div className="flex justify-end mb-2">
                <Button variant="ghost" size="sm" onClick={toggleTheme} className="rounded-full">
                  {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>
              </div>
              <div className={`mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl ${theme === "light" ? "bg-violet-100" : "bg-violet-500/15"} ring-1 ${theme === "light" ? "ring-violet-200" : "ring-violet-300/20"}`}>
                <Lock className={`h-8 w-8 ${theme === "light" ? "text-violet-600" : "text-violet-200"}`} />
              </div>
              <CardTitle className="text-2xl">Admin Login</CardTitle>
              <CardDescription className={themeClasses.textMuted}>Gen-x Digital Holdings - GenX Photo Studio</CardDescription>
            </CardHeader>
            <CardContent>
              {!showForgotPassword ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="admin@example.com" className={`rounded-xl ${themeClasses.input}`} required data-testid="login-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? "text" : "password"} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" className={`rounded-xl pr-10 ${themeClasses.input}`} required data-testid="login-password" />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0" onClick={() => setShowPassword(!showPassword)} data-testid="toggle-password">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Click the eye icon to preview your password</p>
                  </div>
                  <Button type="submit" className="w-full rounded-full bg-violet-500 hover:bg-violet-400 text-white" disabled={loginBusy} data-testid="login-button">
                    {loginBusy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />} Sign In
                  </Button>
                  <Button type="button" variant="link" className={`w-full ${themeClasses.textMuted} hover:text-violet-400`} onClick={() => setShowForgotPassword(true)}>Forgot Password?</Button>
                </form>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center mb-4">
                    <AlertCircle className="mx-auto h-12 w-12 text-amber-400 mb-2" />
                    <p className={`text-sm ${themeClasses.textMuted}`}>Enter your email to receive a password reset</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your@email.com" className={`rounded-xl ${themeClasses.input}`} required />
                  </div>
                  <Button type="submit" className="w-full rounded-full bg-violet-500 hover:bg-violet-400 text-white" disabled={loginBusy}>
                    {loginBusy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send Reset Email
                  </Button>
                  <Button type="button" variant="link" className={`w-full ${themeClasses.textMuted} hover:text-violet-400`} onClick={() => setShowForgotPassword(false)}>Back to Login</Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
        <div className={`text-center pb-8 text-sm ${themeClasses.textMuted}`}>© {new Date().getFullYear()} Gen-x Digital Holdings. All rights reserved.</div>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text}`} data-testid="admin-dashboard">
      <TopNav />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight">Admin Dashboard</h1>
            <p className={`mt-2 ${themeClasses.textMuted}`}>Welcome, <span className="text-violet-400">{currentUser?.name}</span> ({currentUser?.role})</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={toggleTheme} variant="secondary" className={`rounded-full ${theme === "light" ? "bg-gray-100" : "bg-white/5 ring-1 ring-white/10"}`}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button onClick={() => loadDashboard()} variant="secondary" className={`rounded-full ${theme === "light" ? "bg-gray-100" : "bg-white/5 ring-1 ring-white/10"}`} disabled={busy}>
              <RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={handleLogout} variant="secondary" className="rounded-full bg-red-500/10 text-red-400 ring-1 ring-red-500/20 hover:bg-red-500/20">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`${theme === "light" ? "bg-gray-100" : "bg-white/5"} rounded-2xl p-1`}>
            <TabsTrigger value="metrics" className="rounded-xl data-[state=active]:bg-violet-500 data-[state=active]:text-white"><BarChart3 className="mr-2 h-4 w-4" /> Metrics</TabsTrigger>
            <TabsTrigger value="reports" className="rounded-xl data-[state=active]:bg-violet-500 data-[state=active]:text-white" onClick={() => !passportReport && loadPassportReport()}><FileText className="mr-2 h-4 w-4" /> Reports</TabsTrigger>
            <TabsTrigger value="branches" className="rounded-xl data-[state=active]:bg-violet-500 data-[state=active]:text-white"><Key className="mr-2 h-4 w-4" /> Branches</TabsTrigger>
            <TabsTrigger value="staff" className="rounded-xl data-[state=active]:bg-violet-500 data-[state=active]:text-white"><Users className="mr-2 h-4 w-4" /> Staff</TabsTrigger>
          </TabsList>

          {/* Metrics Tab */}
          <TabsContent value="metrics" className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <MetricCard title="Overall Usage" value={metrics?.totals?.overall_usage} icon={TrendingUp} color="emerald" subtitle="Total operations" theme={theme} />
              <MetricCard title="Backgrounds Removed" value={metrics?.totals?.background_removed} icon={ImageMinus} color="violet" subtitle={`Today: ${metrics?.today?.background_removed || 0}`} theme={theme} />
              <MetricCard title="Passport Photos" value={metrics?.totals?.passport_created} icon={Camera} color="cyan" subtitle={`Today: ${metrics?.today?.passport_created || 0}`} theme={theme} />
              <MetricCard title="Photos Restored" value={metrics?.totals?.photo_restored} icon={Sparkles} color="pink" subtitle={`Today: ${metrics?.today?.photo_restored || 0}`} theme={theme} />
              <MetricCard title="Branch Logins" value={metrics?.totals?.branch_logins} icon={Users} color="amber" subtitle={`Today: ${metrics?.today?.branch_logins || 0}`} theme={theme} />
              <MetricCard title="Active Branches" value={metrics?.branches?.active} icon={Calendar} color="blue" subtitle={`Total: ${metrics?.branches?.total || 0}`} theme={theme} />
            </div>
          </TabsContent>

          {/* Reports Tab - Passport Usage by Branch */}
          <TabsContent value="reports" className="space-y-6">
            <Card className={`rounded-3xl ${themeClasses.card}`}>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5 text-cyan-400" />
                    Passport Photo Report
                  </CardTitle>
                  <CardDescription className={themeClasses.textMuted}>
                    Passport photos created by branch (Jamaica Time - UTC-5)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={String(reportDays)} onValueChange={(v) => { setReportDays(Number(v)); loadPassportReport(Number(v)); }}>
                    <SelectTrigger className={`w-32 rounded-xl ${themeClasses.input}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10`}>
                      <SelectItem value="1">Today</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={() => loadPassportReport()} 
                    variant="secondary" 
                    className={`rounded-full ${theme === "light" ? "bg-gray-100" : "bg-white/5 ring-1 ring-white/10"}`}
                    disabled={reportLoading}
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${reportLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary Cards */}
                {passportReport && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className={`rounded-xl p-4 ${theme === "light" ? "bg-cyan-50 border border-cyan-200" : "bg-cyan-500/10 border border-cyan-500/20"}`}>
                      <div className={`text-sm ${theme === "light" ? "text-cyan-700" : "text-cyan-300"}`}>Total Passports</div>
                      <div className={`text-2xl font-bold ${theme === "light" ? "text-cyan-900" : "text-cyan-100"}`}>{passportReport.total_passports || 0}</div>
                    </div>
                    <div className={`rounded-xl p-4 ${theme === "light" ? "bg-violet-50 border border-violet-200" : "bg-violet-500/10 border border-violet-500/20"}`}>
                      <div className={`text-sm ${theme === "light" ? "text-violet-700" : "text-violet-300"}`}>Total Operations</div>
                      <div className={`text-2xl font-bold ${theme === "light" ? "text-violet-900" : "text-violet-100"}`}>{passportReport.total_operations || 0}</div>
                    </div>
                    <div className={`rounded-xl p-4 ${theme === "light" ? "bg-emerald-50 border border-emerald-200" : "bg-emerald-500/10 border border-emerald-500/20"}`}>
                      <div className={`text-sm ${theme === "light" ? "text-emerald-700" : "text-emerald-300"}`}>Active Branches</div>
                      <div className={`text-2xl font-bold ${theme === "light" ? "text-emerald-900" : "text-emerald-100"}`}>{passportReport.summary?.length || 0}</div>
                    </div>
                    <div className={`rounded-xl p-4 ${theme === "light" ? "bg-amber-50 border border-amber-200" : "bg-amber-500/10 border border-amber-500/20"}`}>
                      <div className={`text-sm ${theme === "light" ? "text-amber-700" : "text-amber-300"}`}>Date Range</div>
                      <div className={`text-sm font-medium ${theme === "light" ? "text-amber-900" : "text-amber-100"}`}>Last {reportDays} day{reportDays > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                )}

                {/* Summary by Branch */}
                {passportReport?.summary?.length > 0 && (
                  <div>
                    <h3 className={`text-sm font-medium mb-3 ${themeClasses.text}`}>Summary by Branch</h3>
                    <div className={`rounded-2xl border overflow-hidden ${theme === "light" ? "border-gray-200" : "border-white/10"}`}>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Branch</TableHead>
                            <TableHead>Code</TableHead>
                            <TableHead className="text-right">Operations</TableHead>
                            <TableHead className="text-right">Total Passports</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {passportReport.summary.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{item.branch_name}</TableCell>
                              <TableCell className="font-mono text-sm">{item.branch_code}</TableCell>
                              <TableCell className="text-right">{item.operations}</TableCell>
                              <TableCell className="text-right font-semibold text-cyan-400">{item.total_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Detailed Logs */}
                <div>
                  <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${themeClasses.text}`}>
                    <Clock className="h-4 w-4" /> Detailed Activity Log (Jamaica Time)
                  </h3>
                  <div className={`rounded-2xl border overflow-x-auto ${theme === "light" ? "border-gray-200" : "border-white/10"}`}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date & Time (Jamaica)</TableHead>
                          <TableHead>Branch</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {passportReport?.logs?.length ? passportReport.logs.map((log, idx) => (
                          <TableRow key={idx}>
                            <TableCell className={`font-mono text-sm ${themeClasses.textMuted}`}>{log.timestamp_jamaica}</TableCell>
                            <TableCell className="font-medium">{log.branch_name}</TableCell>
                            <TableCell className="font-mono text-sm">{log.branch_code}</TableCell>
                            <TableCell className="text-right">{log.count}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={4} className={`text-center py-8 ${themeClasses.textMuted}`}>
                              {reportLoading ? (
                                <div className="flex items-center justify-center gap-2">
                                  <RefreshCw className="h-4 w-4 animate-spin" /> Loading report...
                                </div>
                              ) : (
                                "No passport operations recorded in this period"
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branches Tab */}
          <TabsContent value="branches" className="space-y-6">
            <Card className={`rounded-3xl ${themeClasses.card}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Branch Code Management</CardTitle>
                  <CardDescription className={themeClasses.textMuted}>Create and manage branch access codes with names and IP tracking</CardDescription>
                </div>
                <Button onClick={() => setShowCreateBranch(true)} className="rounded-full bg-violet-500 hover:bg-violet-400 text-white">
                  <Plus className="mr-2 h-4 w-4" /> New Branch
                </Button>
              </CardHeader>
              <CardContent>
                <div className={`rounded-2xl border overflow-x-auto ${theme === "light" ? "border-gray-200" : "border-white/10"}`}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Usage</TableHead>
                        <TableHead>Last Used</TableHead>
                        <TableHead>Last IP</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {branches.length ? branches.map((b) => (
                        <TableRow key={b.code}>
                          <TableCell className="font-medium">{b.name || `Branch ${b.code.slice(-4)}`}</TableCell>
                          <TableCell className="font-mono">{b.code}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${b.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                              {b.active ? "Active" : "Inactive"}
                            </span>
                          </TableCell>
                          <TableCell>{b.total_usage || 0}</TableCell>
                          <TableCell className={themeClasses.textMuted}>
                            {b.last_used_at ? new Date(b.last_used_at).toLocaleString() : "Never"}
                          </TableCell>
                          <TableCell>
                            {b.last_used_ip ? (
                              <Button variant="link" size="sm" className="h-auto p-0 text-cyan-400 hover:text-cyan-300" onClick={() => viewBranchHistory(b)}>
                                <Globe className="mr-1 h-3 w-3" />{b.last_used_ip}
                              </Button>
                            ) : <span className={themeClasses.textMuted}>-</span>}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10" onClick={() => startEditBranch(b)} title="Edit Branch">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow><TableCell colSpan={7} className={`text-center ${themeClasses.textMuted}`}>No branch codes yet</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff" className="space-y-6">
            <Card className={`rounded-3xl ${themeClasses.card}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Staff Management</CardTitle>
                  <CardDescription className={themeClasses.textMuted}>Manage staff accounts and credentials</CardDescription>
                </div>
                <Button onClick={() => setShowCreateStaff(true)} className="rounded-full bg-violet-500 hover:bg-violet-400 text-white"><UserPlus className="mr-2 h-4 w-4" /> Add Staff</Button>
              </CardHeader>
              <CardContent>
                <div className={`rounded-2xl border overflow-x-auto ${theme === "light" ? "border-gray-200" : "border-white/10"}`}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead>Last IP</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {staff.length ? staff.map((s) => (
                        <TableRow key={s.email}>
                          <TableCell className="font-medium">{s.name}</TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell><span className={`px-2 py-1 rounded-full text-xs ${s.role === 'admin' ? 'bg-violet-500/20 text-violet-400' : 'bg-zinc-500/20 text-zinc-400'}`}>{s.role}</span></TableCell>
                          <TableCell><span className={`px-2 py-1 rounded-full text-xs ${s.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{s.active ? "Active" : "Inactive"}</span></TableCell>
                          <TableCell className={themeClasses.textMuted}>{s.last_login ? new Date(s.last_login).toLocaleString() : "Never"}</TableCell>
                          <TableCell>
                            {s.last_login_ip ? (
                              <Button variant="link" size="sm" className="h-auto p-0 text-cyan-400 hover:text-cyan-300" onClick={() => viewLoginHistory(s)}>
                                <Globe className="mr-1 h-3 w-3" />{s.last_login_ip}
                              </Button>
                            ) : <span className={themeClasses.textMuted}>-</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" onClick={() => handleResetPassword(s.email, s.name)} title="Reset Password"><Key className="h-4 w-4" /></Button>
                              {s.email !== currentUser?.email && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => handleDeleteStaff(s.email)} title="Delete"><Trash2 className="h-4 w-4" /></Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      )) : <TableRow><TableCell colSpan={7} className={`text-center ${themeClasses.textMuted}`}>No staff members yet</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>© {new Date().getFullYear()} Gen-x Digital Holdings. All rights reserved.</div>
      </div>

      {/* Create Branch Dialog */}
      <Dialog open={showCreateBranch} onOpenChange={setShowCreateBranch}>
        <DialogContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10 rounded-2xl`}>
          <DialogHeader>
            <DialogTitle>Create New Branch</DialogTitle>
            <DialogDescription className={themeClasses.textMuted}>Generate new branch codes with optional name</DialogDescription>
          </DialogHeader>
          <form onSubmit={createBranches} className="space-y-4">
            <div className="space-y-2">
              <Label>Branch Name (optional)</Label>
              <Input value={branchName} onChange={(e) => setBranchName(e.target.value)} placeholder="e.g., Kingston Main, Half Way Tree" className={`rounded-xl ${themeClasses.input}`} />
            </div>
            <div className="space-y-2">
              <Label>Number of Codes</Label>
              <Input type="number" min={1} max={100} value={branchCount} onChange={(e) => setBranchCount(Number(e.target.value))} className={`rounded-xl ${themeClasses.input}`} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreateBranch(false)}>Cancel</Button>
              <Button type="submit" className="bg-violet-500 hover:bg-violet-400 text-white" disabled={busy}>
                {busy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Key className="mr-2 h-4 w-4" />} Generate
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Branch Dialog */}
      <Dialog open={showEditBranch} onOpenChange={setShowEditBranch}>
        <DialogContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10 rounded-2xl`}>
          <DialogHeader>
            <DialogTitle>Edit Branch</DialogTitle>
            <DialogDescription className={themeClasses.textMuted}>Code: {editingBranch?.code}</DialogDescription>
          </DialogHeader>
          <form onSubmit={updateBranch} className="space-y-4">
            <div className="space-y-2">
              <Label>Branch Name</Label>
              <Input value={editBranchName} onChange={(e) => setEditBranchName(e.target.value)} placeholder="Enter branch name" className={`rounded-xl ${themeClasses.input}`} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowEditBranch(false)}>Cancel</Button>
              <Button type="submit" className="bg-violet-500 hover:bg-violet-400 text-white" disabled={busy}>
                {busy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Edit className="mr-2 h-4 w-4" />} Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Branch Usage History Dialog */}
      <Dialog open={showBranchHistory} onOpenChange={setShowBranchHistory}>
        <DialogContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10 rounded-2xl max-w-lg`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> Branch Usage History</DialogTitle>
            <DialogDescription className={themeClasses.textMuted}>{selectedBranchHistory?.name || `Branch ${selectedBranchHistory?.code}`}</DialogDescription>
          </DialogHeader>
          {selectedBranchHistory && (
            <div className={`rounded-xl border overflow-hidden ${theme === "light" ? "border-gray-200" : "border-white/10"}`}>
              <Table>
                <TableHeader><TableRow><TableHead>Date & Time</TableHead><TableHead>IP Address</TableHead></TableRow></TableHeader>
                <TableBody>
                  {selectedBranchHistory.usage_history?.length ? [...selectedBranchHistory.usage_history].reverse().map((use, idx) => (
                    <TableRow key={idx}>
                      <TableCell className={themeClasses.textMuted}>{new Date(use.timestamp).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-cyan-400">{use.ip_address}</TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={2} className={`text-center ${themeClasses.textMuted}`}>No usage history</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setShowBranchHistory(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Staff Dialog */}
      <Dialog open={showCreateStaff} onOpenChange={setShowCreateStaff}>
        <DialogContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10 rounded-2xl`}>
          <DialogHeader>
            <DialogTitle>Add New Staff Member</DialogTitle>
            <DialogDescription className={themeClasses.textMuted}>Credentials will be generated automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} placeholder="John Doe" className={`rounded-xl ${themeClasses.input}`} required /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={newStaffEmail} onChange={(e) => setNewStaffEmail(e.target.value)} placeholder="john@example.com" className={`rounded-xl ${themeClasses.input}`} required /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newStaffRole} onValueChange={setNewStaffRole}>
                <SelectTrigger className={`rounded-xl ${themeClasses.input}`}><SelectValue /></SelectTrigger>
                <SelectContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10`}>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreateStaff(false)}>Cancel</Button>
              <Button type="submit" className="bg-violet-500 hover:bg-violet-400 text-white" disabled={busy}>
                {busy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Create & Preview Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10 rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5" /> Email Preview</DialogTitle>
            <DialogDescription className={themeClasses.textMuted}>Review before sending to {emailPreviewData?.to_email}</DialogDescription>
          </DialogHeader>
          {emailPreviewData && (
            <div className="space-y-4">
              <div className={`rounded-xl ${theme === "light" ? "bg-gray-100" : "bg-white/5"} p-4 space-y-2`}>
                <p className="text-sm"><span className={themeClasses.textMuted}>To:</span> {emailPreviewData.to_email}</p>
                <p className="text-sm"><span className={themeClasses.textMuted}>Subject:</span> {emailPreviewData.subject}</p>
              </div>
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="bg-white text-black p-4 max-h-[400px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: emailPreviewData.html_content }} />
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => { setShowEmailPreview(false); setEmailPreviewData(null); }}>Cancel</Button>
            <Button onClick={handleSendEmail} className="bg-emerald-500 hover:bg-emerald-400 text-white" disabled={emailSending}>
              {emailSending ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Login History Dialog */}
      <Dialog open={showLoginHistory} onOpenChange={setShowLoginHistory}>
        <DialogContent className={`${theme === "light" ? "bg-white" : "bg-zinc-900"} border-white/10 rounded-2xl max-w-lg`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> Login History</DialogTitle>
            <DialogDescription className={themeClasses.textMuted}>{selectedStaffHistory?.name} ({selectedStaffHistory?.email})</DialogDescription>
          </DialogHeader>
          {selectedStaffHistory && (
            <div className={`rounded-xl border overflow-hidden ${theme === "light" ? "border-gray-200" : "border-white/10"}`}>
              <Table>
                <TableHeader><TableRow><TableHead>Date & Time</TableHead><TableHead>IP Address</TableHead></TableRow></TableHeader>
                <TableBody>
                  {selectedStaffHistory.login_history?.length ? [...selectedStaffHistory.login_history].reverse().map((login, idx) => (
                    <TableRow key={idx}>
                      <TableCell className={themeClasses.textMuted}>{new Date(login.timestamp).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-cyan-400">{login.ip_address}</TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={2} className={`text-center ${themeClasses.textMuted}`}>No login history</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setShowLoginHistory(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, color, subtitle, theme }) {
  const colorClasses = {
    emerald: theme === "light" ? "bg-emerald-100 ring-emerald-200 text-emerald-600" : "bg-emerald-500/15 ring-emerald-300/20 text-emerald-200",
    violet: theme === "light" ? "bg-violet-100 ring-violet-200 text-violet-600" : "bg-violet-500/15 ring-violet-300/20 text-violet-200",
    cyan: theme === "light" ? "bg-cyan-100 ring-cyan-200 text-cyan-600" : "bg-cyan-500/15 ring-cyan-300/20 text-cyan-200",
    pink: theme === "light" ? "bg-pink-100 ring-pink-200 text-pink-600" : "bg-pink-500/15 ring-pink-300/20 text-pink-200",
    amber: theme === "light" ? "bg-amber-100 ring-amber-200 text-amber-600" : "bg-amber-500/15 ring-amber-300/20 text-amber-200",
    blue: theme === "light" ? "bg-blue-100 ring-blue-200 text-blue-600" : "bg-blue-500/15 ring-blue-300/20 text-blue-200",
  };
  return (
    <Card className={`rounded-2xl ${theme === "light" ? "bg-white border-gray-200" : "bg-zinc-950/50 border-white/10"}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm ${theme === "light" ? "text-gray-500" : "text-zinc-400"}`}>{title}</p>
            <p className={`mt-1 text-3xl font-bold ${theme === "light" ? "text-gray-900" : "text-zinc-100"}`}>{value?.toLocaleString() || 0}</p>
            {subtitle && <p className={`mt-1 text-xs ${theme === "light" ? "text-gray-400" : "text-zinc-500"}`}>{subtitle}</p>}
          </div>
          <div className={`grid h-12 w-12 place-items-center rounded-xl ring-1 ${colorClasses[color]}`}><Icon className="h-6 w-6" /></div>
        </div>
      </CardContent>
    </Card>
  );
}
